import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Upload, FileText, Image as ImageIcon, Trash2, Loader2, IdCard, Download } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DOC_LABELS = { photo: 'Client Photo', id_document: 'Scanned ID', other: 'Document' };

const ResidentFiles = ({ residentId, residentName, open, onOpenChange }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [docType, setDocType] = useState('photo');
  const fileInputRef = useRef(null);
  const token = localStorage.getItem('token');

  const fetchFiles = useCallback(async () => {
    if (!residentId) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/residents/${residentId}/files`);
      setFiles(res.data);
    } catch (e) {
      toast.error('Failed to load files');
    } finally {
      setLoading(false);
    }
  }, [residentId]);

  useEffect(() => { if (open) fetchFiles(); }, [open, fetchFiles]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error('File too large (max 25 MB)'); return; }
    const form = new FormData();
    form.append('file', file);
    form.append('doc_type', docType);
    setUploading(true);
    try {
      await axios.post(`${API}/residents/${residentId}/files`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${DOC_LABELS[docType]} uploaded`);
      fetchFiles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDelete = async (fileId) => {
    if (!window.confirm('Delete this file? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/residents/files/${fileId}`);
      toast.success('File deleted');
      setFiles((prev) => prev.filter((f) => f.id !== fileId));
    } catch (e) {
      toast.error('Failed to delete file');
    }
  };

  const isImage = (f) => (f.content_type || '').startsWith('image/');
  const fileUrl = (id) => `${API}/files/${id}/download?auth=${token}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="resident-files-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><IdCard className="h-5 w-5 text-indigo-600" /> Files & Photos — {residentName}</DialogTitle>
          <DialogDescription>Upload client photos and scanned ID documents. Stored securely; not on local disk.</DialogDescription>
        </DialogHeader>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-end gap-3 p-4 bg-gray-50 rounded-lg">
          <div className="flex-1">
            <label className="text-sm font-medium text-gray-700">Document type</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="file-doctype-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="photo">Client Photo</SelectItem>
                <SelectItem value="id_document">Scanned ID</SelectItem>
                <SelectItem value="other">Other Document</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <input ref={fileInputRef} type="file" accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt" onChange={handleUpload} className="hidden" data-testid="file-input" />
          <Button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="bg-indigo-600 hover:bg-indigo-700" data-testid="upload-file-btn">
            {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="h-4 w-4 mr-2" />Upload File</>}
          </Button>
        </div>

        <div className="mt-2">
          {loading ? (
            <div className="text-center py-8 text-gray-400"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : files.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-40" />
              <p>No files yet. Upload a photo or scanned ID above.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
              {files.map((f) => (
                <div key={f.id} className="border rounded-lg overflow-hidden bg-white" data-testid={`file-card-${f.id}`}>
                  <div className="h-36 bg-gray-100 flex items-center justify-center overflow-hidden">
                    {isImage(f) ? (
                      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(f.id)} alt={f.original_filename} className="h-36 w-full object-cover" />
                      </a>
                    ) : (
                      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="flex flex-col items-center text-gray-500">
                        <FileText className="h-10 w-10" /><span className="text-xs mt-1">PDF</span>
                      </a>
                    )}
                  </div>
                  <div className="p-2 space-y-1">
                    <Badge variant="outline" className="text-xs">{DOC_LABELS[f.doc_type] || 'Document'}</Badge>
                    <p className="text-xs text-gray-600 truncate" title={f.original_filename}>{f.original_filename}</p>
                    <div className="flex items-center justify-between">
                      <a href={fileUrl(f.id)} target="_blank" rel="noreferrer" className="text-indigo-600 hover:text-indigo-800" title="Open"><Download className="h-4 w-4" /></a>
                      <button onClick={() => handleDelete(f.id)} className="text-red-500 hover:text-red-700" data-testid={`delete-file-${f.id}`} title="Delete"><Trash2 className="h-4 w-4" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ResidentFiles;
